
import React, { useState } from "react";
import { PartFilter, PartSubcategory } from "@/data/parts";
import { SidebarGroup, SidebarGroupLabel, SidebarGroupContent } from "@/components/ui/sidebar";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { useToast } from "@/hooks/use-toast";
import SpecialFiltersBadges from "@/components/sidebar/filters/SpecialFiltersBadges";
import RequirementsFilterBadges from "@/components/sidebar/filters/RequirementsFilterBadges";
import SavePreferencesButton from "@/components/sidebar/filters/SavePreferencesButton";

interface SpecialFiltersGroupProps {
  activeFilters: PartFilter[];
  toggleFilter: (filter: PartFilter) => void;
  selectedSubcategory: PartSubcategory | "all";
}

const SpecialFiltersGroup = ({ activeFilters, toggleFilter, selectedSubcategory }: SpecialFiltersGroupProps) => {
  const { user } = useAuth();
  const { toast } = useToast();
  const [saving, setSaving] = useState(false);

  const saveRequirementsPreference = async () => {
    if (!user) {
      toast({
        title: "Not logged in",
        description: "You need to be logged in to save preferences",
        variant: "destructive",
      });
      return;
    }

    setSaving(true);
    try {
      const requirementsPrefs = {
        requires_root: activeFilters.includes("requires-root"),
        requires_tools: activeFilters.includes("requires-tools"),
        wiring: activeFilters.includes("wiring"),
        requires_printing: activeFilters.includes("requires-printing"),
        requires_purchase: activeFilters.includes("requires-purchase")
      };

      const { error } = await supabase
        .from('user_preferences')
        .upsert({ 
          id: user.id,
          requirements_prefs: requirementsPrefs 
        });

      if (error) throw error;
      
      toast({
        title: "Preferences saved",
        description: "Your requirements visibility preferences have been saved",
      });
    } catch (error) {
      console.error("Error saving requirements preferences:", error);
      toast({
        title: "Error",
        description: "Failed to save your preferences",
        variant: "destructive",
      });
    } finally {
      setSaving(false);
    }
  };
  
  return (
    <>
      <SidebarGroup>
        <SidebarGroupLabel>Special Filters</SidebarGroupLabel>
        <SidebarGroupContent>
          <SpecialFiltersBadges 
            activeFilters={activeFilters} 
            toggleFilter={toggleFilter} 
            selectedSubcategory={selectedSubcategory}
          />
        </SidebarGroupContent>
      </SidebarGroup>

      <SidebarGroup>
        <div className="flex items-center justify-between pr-3">
          <SidebarGroupLabel className="text-xs font-normal">Requirements</SidebarGroupLabel>
          <div className="flex justify-end">
            <SavePreferencesButton 
              onClick={saveRequirementsPreference} 
              saving={saving}
            />
          </div>
        </div>
        <SidebarGroupContent>
          <RequirementsFilterBadges 
            activeFilters={activeFilters} 
            toggleFilter={toggleFilter}
          />
        </SidebarGroupContent>
      </SidebarGroup>
    </>
  );
};

export default SpecialFiltersGroup;
